// SDK ⇄ app boundary for the session forms: the enquêtes, and the questionnaires orders calls assessments.
//
// The builder edits orders' own activities (IActivityDraft): this file turns them into its commands, and the respondent
// and tracking payloads into what the wizard and the session tab render. The app's enums carry orders' values, so
// nothing here translates a type.
//
// A questionnaire question's `description` is its « Éléments de correction »: orders withholds it until the answer is
// saved, and the questionnaire payload carries no help text.
import type {
  ActivitiesQuizResponse,
  ActivitiesResponseQuiz,
  ActivitiesUpdateDto,
  ActivitiesUpdateQuizDto,
  AssessmentAnswerResult,
  AssessmentQuestionElement,
  AssessmentQuestionElementSavedAnswer,
  CreateFormTemplateDto,
  CreateSessionFormDto,
  EmployeeSessionFormAssessmentCard,
  FormRespondPayload,
  FormRespondQuestion,
  FormTemplateDetailResponse,
  FormTemplateResponse,
  FormTemplateSupportResponse,
  SaveSessionFormAssessmentAnswerDto,
  SessionFormAssessmentRecipientRow,
  SessionFormAssessmentRespondPayload,
  SessionFormAssessmentResponse,
  SessionFormRecipientRow,
  SessionFormsTrackingResponse,
  SessionFormSummary,
  SessionFormSummaryOffset,
  SubmitFormAnswerDto,
  UpdateFormTemplateDto,
} from "@simbelapp/order-sdk";
import {
  ActivitiesResponseTypeEnum,
  AssessmentGapsCorrectionSubTypeEnum,
  AssessmentMatchingCorrectionSubTypeEnum,
  AssessmentMultipleChoiceCorrectionSubTypeEnum,
  AssessmentQuestionElementTypeEnum,
  CreateSessionFormDtoOffsetAnchorEnum,
  CreateSessionFormDtoOffsetDirectionEnum,
  CreateSessionFormDtoOffsetUnitEnum,
  CreateSessionFormDtoSendModeEnum,
  EmployeeSessionFormAssessmentCardStatusEnum,
  SessionFormAssessmentRecipientRowStatusEnum,
  SessionFormRecipientRowStatusEnum,
  SessionFormSummaryOffsetAnchorEnum,
  SessionFormSummaryOffsetDirectionEnum,
  SessionFormSummaryOffsetUnitEnum,
  SessionFormSummaryStatusEnum,
} from "@simbelapp/order-sdk";
import { QuizTypeEnum } from "~/utils/enums/activities.enums";
import {
  EvaluationTypeEnum,
  SessionSurveyOffsetAnchorEnum,
  SessionSurveyOffsetDirectionEnum,
  SessionSurveyOffsetUnitEnum,
  SessionSurveyParticipantStatusEnum,
  SessionSurveyRecipientEnum,
  SessionSurveySendModeEnum,
  SessionSurveyStatusEnum,
} from "~/utils/enums/evaluations.enums";
import { isQuizType } from "~/utils/helpers/quiz.helpers";
import type { IActivityDraft } from "~/utils/interfaces/activities-interfaces";
import type { IPlayerActivity, IQuizAnswerValue, IQuizCorrection } from "~/utils/interfaces/quiz-interfaces";
import type {
  IEvaluationTemplate,
  IEvaluationTemplateDraft,
  ICreateSessionSurveyPayload,
  ISessionSurvey,
  ISessionSurveyAnswer,
  ISessionSurveyModele,
  ISessionSurveyParticipant,
  ISessionSurveyRecap,
  ISessionSurveySchedule,
  IMyQuestionnaireStep,
} from "~/utils/interfaces/evaluations-interfaces";

/** orders' template type, which the app's enum values the same. */
export const toEvaluationType = (type: string): EvaluationTypeEnum =>
  Object.values(EvaluationTypeEnum).find((surveyType) => surveyType === type) ?? EvaluationTypeEnum.DIRECT;

export const toQuizType = (subType: string): QuizTypeEnum | null => (isQuizType(subType) ? subType : null);

// --- template authoring (Settings builder) -----------------------------------
const byOrder = (a: { order?: number | null }, b: { order?: number | null }) => (a.order ?? 0) - (b.order ?? 0);

const withSortedAnswers = (activity: IActivityDraft): IActivityDraft =>
  activity.quiz
    ? { ...activity, quiz: { ...activity.quiz, answers: [...activity.quiz.answers].sort(byOrder) } }
    : activity;

export const questionActivity = (quiz: ActivitiesQuizResponse | ActivitiesResponseQuiz): IActivityDraft =>
  withSortedAnswers({ activityId: quiz.activityId, type: ActivitiesResponseTypeEnum.Quiz, title: null, quiz });

/** Full template → the builder's draft. A questionnaire reads its ordered elements, an enquête its questions. */
export function mapTemplateDetailToDraft(detail: FormTemplateDetailResponse): IEvaluationTemplateDraft {
  // A SCORM support has no builder form, so it is left out of the draft.
  const activities: IActivityDraft[] = detail.elements
    ? detail.elements.filter((element) => element.type !== ActivitiesResponseTypeEnum.Scorm).map(withSortedAnswers)
    : [...detail.questions].sort(byOrder).map(questionActivity);
  return {
    id: detail.formTemplateId,
    name: detail.name,
    type: toEvaluationType(detail.type),
    validationThreshold: detail.validationThreshold ?? null,
    activities,
  };
}

/** Template list row → the Settings table projection (name + type). */
export function mapTemplateListItem(template: FormTemplateResponse): IEvaluationTemplate {
  return { id: template.formTemplateId, name: template.name, type: toEvaluationType(template.type) };
}

/** Active templates of one type → a modèle dropdown. */
export function mapTemplateOption(template: FormTemplateResponse): ISessionSurveyModele {
  return {
    id: template.formTemplateId,
    name: template.name,
    type: toEvaluationType(template.type),
    validationThreshold: template.validationThreshold ?? null,
  };
}

/** Draft header → create DTO. The threshold is a questionnaire's only: orders rejects it on an enquête. */
export function toCreateFormTemplateDto(
  name: string,
  type: EvaluationTypeEnum,
  validationThreshold: number | null,
): CreateFormTemplateDto {
  return { name, type, ...(type === EvaluationTypeEnum.QUESTIONNAIRE ? { validationThreshold } : {}) };
}

export function toUpdateFormTemplateDto(draft: IEvaluationTemplateDraft): UpdateFormTemplateDto {
  return {
    name: draft.name.trim(),
    ...(draft.type === EvaluationTypeEnum.QUESTIONNAIRE ? { validationThreshold: draft.validationThreshold } : {}),
  };
}

// The key in the two shapes orders' buildQuizAnswerRows reads: `options` for an un-scored MC, `answers` for a graded
// type. Sending the other one silently persists no key.
function quizKey(quiz: ActivitiesResponseQuiz): Pick<ActivitiesUpdateQuizDto, "options" | "answers"> {
  // orders hides a question holding one blank option from its respondents: a row left empty is not sent.
  const answers = quiz.answers.filter(
    (answer) => quiz.subType === QuizTypeEnum.GAP_FILLING_QUESTION || answer.answer?.trim() || answer.question?.trim(),
  );
  switch (quiz.subType) {
    case QuizTypeEnum.MULTIPLE_CHOICE_UNSCORED:
      return { options: answers.map((answer) => answer.answer ?? "") };
    case QuizTypeEnum.MULTIPLE_CHOICE_QUESTION:
      return {
        answers: answers.map((answer, order) => ({
          order,
          answer: answer.answer ?? "",
          isRightAnswer: !!answer.isRightAnswer,
        })),
      };
    case QuizTypeEnum.ASSOCIATION_QUESTION:
      return {
        answers: answers.map((answer, order) => ({
          order,
          question: answer.question ?? "",
          answer: answer.answer ?? "",
        })),
      };
    case QuizTypeEnum.GAP_FILLING_QUESTION:
      return {
        answers: answers.map((answer, order) => ({
          order,
          gapAnswers: (answer.gapAnswers ?? []).filter((spelling) => spelling.trim()),
        })),
      };
    default:
      return {};
  }
}

function quizFields(quiz: ActivitiesResponseQuiz): ActivitiesUpdateQuizDto {
  return {
    title: quiz.title ?? "",
    description: quiz.description || null,
    required: !!quiz.required,
    ...(quiz.subType === QuizTypeEnum.RATING
      ? { minLabel: quiz.minLabel ?? null, maxLabel: quiz.maxLabel ?? null }
      : {}),
    ...quizKey(quiz),
  };
}

function supportFields(activity: IActivityDraft): ActivitiesUpdateDto {
  const { type } = activity;
  return {
    title: activity.title ?? "",
    ...(type === ActivitiesResponseTypeEnum.Text ? { content: activity.content ?? "" } : {}),
    ...(type === ActivitiesResponseTypeEnum.Video || type === ActivitiesResponseTypeEnum.Document
      ? { url: activity.url ?? "" }
      : {}),
    ...(type === ActivitiesResponseTypeEnum.Document ? { documentName: activity.documentName ?? null } : {}),
    ...(type === ActivitiesResponseTypeEnum.Video ? { videoId: activity.videoId ?? null } : {}),
    ...(type === ActivitiesResponseTypeEnum.Video || type === ActivitiesResponseTypeEnum.Embed
      ? { embedUrl: activity.embedUrl ?? "" }
      : {}),
  };
}

function changedFields<T extends object>(next: T, before: T): Partial<T> {
  const changed: Partial<T> = {};
  for (const key of Object.keys(next) as (keyof T)[]) {
    if (JSON.stringify(next[key]) !== JSON.stringify(before[key])) changed[key] = next[key];
  }
  return changed;
}

/**
 * A question's quiz edit: every field for a new question, only what changed against the saved one otherwise, null when
 * nothing did. Resending an unchanged key is refused on an answered enquête, and regenerates a questionnaire's option
 * ids under the learners answering it.
 */
export function toUpdateQuizDto(
  quiz: ActivitiesResponseQuiz,
  saved?: ActivitiesResponseQuiz | null,
): ActivitiesUpdateQuizDto | null {
  const next = quizFields(quiz);
  if (!saved?.quizId) return next;
  const changed = changedFields(next, quizFields(saved));
  if (saved.subType !== quiz.subType) changed.subType = quiz.subType;
  return Object.keys(changed).length ? changed : null;
}

/** A support's edit, on the same rule; it also carries its kind when the saved activity had another. */
export function toUpdateActivityDto(
  activity: IActivityDraft,
  saved?: IActivityDraft | null,
): ActivitiesUpdateDto | null {
  const next = supportFields(activity);
  if (!saved) return next;
  const changed: ActivitiesUpdateDto = changedFields(next, supportFields(saved));
  if (saved.type !== activity.type) changed.type = activity.type;
  return Object.keys(changed).length ? changed : null;
}

// --- respondent (enquête link and questionnaire player) ----------------------
/** A support, as the learner goes through it. */
function mapPlayerSupport(element: FormTemplateSupportResponse): IPlayerActivity {
  return {
    id: element.activityId,
    type: element.type,
    subType: null,
    title: element.title ?? "",
    content: element.content ?? undefined,
    url: element.url ?? undefined,
    documentName: element.documentName ?? undefined,
    embedUrl: element.embedUrl ?? undefined,
    videoId: element.videoId ?? undefined,
  };
}

/** A public enquête question → the activité the wizard renders. */
function mapRespondQuestion(question: FormRespondQuestion): IPlayerActivity {
  return {
    id: question.activityId,
    type: ActivitiesResponseTypeEnum.Quiz,
    subType: toQuizType(question.subType),
    title: question.title ?? "",
    description: question.description ?? undefined,
    required: !!question.required,
    minLabel: question.minLabel ?? undefined,
    maxLabel: question.maxLabel ?? undefined,
    // A choice is answered by its id: a row without one cannot be submitted.
    options: question.answers?.flatMap((answer) =>
      answer.answerId ? [{ key: answer.answerId, answer: answer.answer ?? "" }] : [],
    ),
    pairPrompts: question.pairPrompts?.map((prompt) => ({ id: prompt.id, label: prompt.label, order: prompt.order })),
    pairCandidates: question.pairCandidates ?? undefined,
    gaps: question.gaps?.map((gap) => gap.order),
  };
}

/** Public-capture payload → the activités the wizard renders (ordered) + display name/type. */
export function mapRespondPayload(payload: FormRespondPayload): {
  questions: IPlayerActivity[];
  formName: string;
  type: EvaluationTypeEnum;
} {
  return {
    questions: [...payload.questions].sort(byOrder).map(mapRespondQuestion),
    formName: payload.formName,
    type: toEvaluationType(payload.type),
  };
}

function mapAssessmentQuestion(element: AssessmentQuestionElement): IPlayerActivity {
  const { input } = element;
  return {
    id: element.questionId,
    type: ActivitiesResponseTypeEnum.Quiz,
    subType: toQuizType(element.subType),
    title: element.title,
    required: element.required,
    minLabel: input.minLabel ?? undefined,
    maxLabel: input.maxLabel ?? undefined,
    options: input.options?.map((option) => ({ key: option.id, answer: option.label })),
    pairPrompts: input.pairPrompts?.map((prompt) => ({ id: prompt.id, label: prompt.label, order: prompt.order })),
    pairCandidates: input.pairCandidates ?? undefined,
    gaps: input.gaps?.map((gap) => gap.order),
  };
}

/** A saved answer → the value the wizard's inputs hold (see IQuizAnswerValue). */
function savedAnswerValue(subType: QuizTypeEnum | null, saved: AssessmentQuestionElementSavedAnswer): IQuizAnswerValue {
  switch (subType) {
    case QuizTypeEnum.RATING:
      return saved.value ? Number(saved.value) : "";
    case QuizTypeEnum.MULTIPLE_CHOICE_UNSCORED:
    case QuizTypeEnum.MULTIPLE_CHOICE_QUESTION:
      return saved.selectedOptions ?? [];
    case QuizTypeEnum.ASSOCIATION_QUESTION:
      return Object.fromEntries((saved.pairs ?? []).map((pair) => [pair.leftId, pair.chosen ?? ""]));
    case QuizTypeEnum.GAP_FILLING_QUESTION:
      return Object.fromEntries((saved.gapAnswers ?? []).map((gap) => [String(gap.order), gap.value ?? ""]));
    default:
      return saved.value ?? "";
  }
}

/** A saved answer's result → the verdict the player shows. orders grades; a free question comes back with no key. */
export function mapAssessmentCorrection(
  result: Pick<AssessmentAnswerResult, "questionId" | "questionScore" | "correction">,
): IQuizCorrection {
  const { correction } = result;
  const base: IQuizCorrection = {
    questionId: result.questionId,
    passedRate: result.questionScore ?? null,
    choices: [],
    rightOptionIds: [],
    rightOptionLabels: [],
    pairs: [],
    gaps: [],
    explanation: correction?.explanation ?? null,
  };
  switch (correction?.subType) {
    case AssessmentMultipleChoiceCorrectionSubTypeEnum.MultipleChoiceQuestion: {
      const right = correction.choices.filter((choice) => choice.expected);
      return {
        ...base,
        choices: correction.choices.map((choice) => ({ key: choice.id, answer: choice.label })),
        rightOptionIds: right.map((choice) => choice.id),
        rightOptionLabels: right.map((choice) => choice.label),
      };
    }
    case AssessmentMatchingCorrectionSubTypeEnum.AssociationQuestion:
      return {
        ...base,
        pairs: [...correction.pairs].sort(byOrder).map((pair) => ({
          leftId: pair.leftId,
          prompt: pair.prompt,
          chosen: pair.chosen ?? null,
          expected: pair.expected,
          correct: pair.correct,
        })),
      };
    case AssessmentGapsCorrectionSubTypeEnum.GapFillingQuestion:
      return {
        ...base,
        gaps: correction.gaps.map((gap) => ({
          order: gap.order,
          value: gap.value ?? null,
          accepted: gap.acceptedValues,
          correct: gap.correct,
        })),
      };
    default:
      return base;
  }
}

/**
 * A questionnaire's own payload → the wizard: its ordered activités, and every question already saved with its answer
 * and its verdict. Saved means validated: the wizard resumes on the first question that is not.
 */
export function mapAssessmentPayload(payload: SessionFormAssessmentRespondPayload): {
  questions: IPlayerActivity[];
  formName: string;
  answers: Record<string, IQuizAnswerValue>;
  corrections: Record<string, IQuizCorrection>;
} {
  const answers: Record<string, IQuizAnswerValue> = {};
  const corrections: Record<string, IQuizCorrection> = {};
  const questions = payload.elements.map((element) => {
    if (element.type !== AssessmentQuestionElementTypeEnum.Quiz) return mapPlayerSupport(element);
    const question = mapAssessmentQuestion(element);
    if (element.savedAnswer) {
      answers[question.id] = savedAnswerValue(question.subType, element.savedAnswer);
      corrections[question.id] = mapAssessmentCorrection(element);
    }
    return question;
  });
  return { questions, formName: payload.templateName, answers, corrections };
}

/** The learner's own questionnaire card → its encart on the session page. */
export function mapMyQuestionnaireCard(card: EmployeeSessionFormAssessmentCard): IMyQuestionnaireStep {
  return {
    id: card.sessionFormId,
    templateName: card.templateName,
    date: card.date,
    recipientId: card.recipientId,
    isCompleted: card.status === EmployeeSessionFormAssessmentCardStatusEnum.Completed,
    score: card.score,
  };
}

/** A session's questionnaire seen by someone who does not answer it (the « Vue utilisateur » preview). */
export function mapQuestionnairePreviewCard(questionnaire: SessionFormAssessmentResponse): IMyQuestionnaireStep {
  return {
    id: questionnaire.sessionFormId,
    templateName: questionnaire.templateName,
    date: questionnaire.date,
    recipientId: null,
    isCompleted: false,
    score: null,
  };
}

/** A questionnaire step's participant → a row of the participants table an enquête shares. */
export function mapAssessmentRecipientRow(row: SessionFormAssessmentRecipientRow): ISessionSurveyParticipant {
  return {
    id: row.recipientId,
    name: row.attendeeName,
    attendeeName: row.attendeeName,
    managerName: null,
    userId: row.attendeeId,
    status:
      row.status === SessionFormAssessmentRecipientRowStatusEnum.Completed
        ? SessionSurveyParticipantStatusEnum.COMPLETED
        : SessionSurveyParticipantStatusEnum.PENDING,
    date: row.date,
    respondedAt: row.responseDate,
    score: row.score,
    answers: [],
  };
}

/**
 * App answer → the field family its type answers in, the one the enquête submit and the questionnaire save share:
 * RATING / OPEN → `value` · choices, scored or not → `selectedOptions` (option ids) · ASSOCIATION → `pairs`
 * [{leftId, chosen}] · GAP_FILLING → `gapAnswers` [{order, value}]. Filling another one is accepted and stores nothing.
 */
export function toAnswerFields(answer: Omit<ISessionSurveyAnswer, "questionId">): SaveSessionFormAssessmentAnswerDto {
  const asMap = (value: IQuizAnswerValue): Record<string, string> =>
    value && typeof value === "object" && !Array.isArray(value) ? value : {};

  switch (answer.subType) {
    case QuizTypeEnum.MULTIPLE_CHOICE_UNSCORED:
    case QuizTypeEnum.MULTIPLE_CHOICE_QUESTION:
      return { selectedOptions: Array.isArray(answer.value) ? answer.value : [] };
    case QuizTypeEnum.ASSOCIATION_QUESTION:
      return { pairs: Object.entries(asMap(answer.value)).map(([leftId, chosen]) => ({ leftId, chosen })) };
    case QuizTypeEnum.GAP_FILLING_QUESTION:
      return {
        gapAnswers: Object.entries(asMap(answer.value)).map(([order, value]) => ({ order: Number(order), value })),
      };
    default:
      return { value: answer.value == null ? null : String(answer.value) };
  }
}

export function toSubmitAnswerDto(answer: ISessionSurveyAnswer): SubmitFormAnswerDto {
  return { questionId: answer.questionId, ...toAnswerFields(answer) };
}

// --- session tracking (Enquêtes tab) -----------------------------------------
// Offset enums share the SDK's string values; explicit maps keep it typesafe without casts.
const OFFSET_UNIT_TO_SDK = {
  hour: CreateSessionFormDtoOffsetUnitEnum.Hour,
  day: CreateSessionFormDtoOffsetUnitEnum.Day,
} as const;
const OFFSET_DIRECTION_TO_SDK = {
  before: CreateSessionFormDtoOffsetDirectionEnum.Before,
  after: CreateSessionFormDtoOffsetDirectionEnum.After,
} as const;
const OFFSET_ANCHOR_TO_SDK = {
  session_start: CreateSessionFormDtoOffsetAnchorEnum.Start,
  session_end: CreateSessionFormDtoOffsetAnchorEnum.End,
} as const;

// Reverse maps (SDK summary → app) — used to prefill the "Modifier" modal from getSessionFormsTracking.
const OFFSET_UNIT_FROM_SDK: Record<SessionFormSummaryOffsetUnitEnum, SessionSurveyOffsetUnitEnum> = {
  [SessionFormSummaryOffsetUnitEnum.Hour]: SessionSurveyOffsetUnitEnum.HOUR,
  [SessionFormSummaryOffsetUnitEnum.Day]: SessionSurveyOffsetUnitEnum.DAY,
};
const OFFSET_DIRECTION_FROM_SDK: Record<SessionFormSummaryOffsetDirectionEnum, SessionSurveyOffsetDirectionEnum> = {
  [SessionFormSummaryOffsetDirectionEnum.Before]: SessionSurveyOffsetDirectionEnum.BEFORE,
  [SessionFormSummaryOffsetDirectionEnum.After]: SessionSurveyOffsetDirectionEnum.AFTER,
};
const OFFSET_ANCHOR_FROM_SDK: Record<SessionFormSummaryOffsetAnchorEnum, SessionSurveyOffsetAnchorEnum> = {
  [SessionFormSummaryOffsetAnchorEnum.Start]: SessionSurveyOffsetAnchorEnum.SESSION_START,
  [SessionFormSummaryOffsetAnchorEnum.End]: SessionSurveyOffsetAnchorEnum.SESSION_END,
};
// The app tab has only Programmée / Envoyée; a form-level `completed` (all responded) still reads Envoyée, and
// `pending` (a questionnaire's, never on this survey-only route) reads as not sent yet.
const SURVEY_STATUS_FROM_SDK: Record<SessionFormSummaryStatusEnum, SessionSurveyStatusEnum> = {
  [SessionFormSummaryStatusEnum.Pending]: SessionSurveyStatusEnum.SCHEDULED,
  [SessionFormSummaryStatusEnum.Scheduled]: SessionSurveyStatusEnum.SCHEDULED,
  [SessionFormSummaryStatusEnum.Sent]: SessionSurveyStatusEnum.SENT,
  [SessionFormSummaryStatusEnum.Completed]: SessionSurveyStatusEnum.SENT,
};

function mapOffsetToSchedule(offset?: SessionFormSummaryOffset | null): ISessionSurveySchedule | null {
  if (!offset) return null;
  return {
    amount: offset.n,
    unit: OFFSET_UNIT_FROM_SDK[offset.unit],
    direction: OFFSET_DIRECTION_FROM_SDK[offset.direction],
    anchor: OFFSET_ANCHOR_FROM_SDK[offset.anchor],
  };
}

const PARTICIPANT_STATUS_FROM_SDK: Record<SessionFormRecipientRowStatusEnum, SessionSurveyParticipantStatusEnum> = {
  [SessionFormRecipientRowStatusEnum.Pending]: SessionSurveyParticipantStatusEnum.PENDING,
  [SessionFormRecipientRowStatusEnum.Scheduled]: SessionSurveyParticipantStatusEnum.SCHEDULED,
  [SessionFormRecipientRowStatusEnum.Sent]: SessionSurveyParticipantStatusEnum.SENT,
  [SessionFormRecipientRowStatusEnum.Completed]: SessionSurveyParticipantStatusEnum.COMPLETED,
};

function mapRecipientRow(row: SessionFormRecipientRow): ISessionSurveyParticipant {
  return {
    id: row.id,
    token: row.id, // the recipient id IS the personalized-link token
    name: row.name,
    // `name` is the addressee — the MANAGER for an à-froid enquête — which is why the table reads
    // attendeeName/managerName instead (see ISessionSurveyParticipant).
    attendeeName: row.attendeeName,
    managerName: row.managerName ?? null,
    userId: row.userId ?? null,
    status: PARTICIPANT_STATUS_FROM_SDK[row.status] ?? SessionSurveyParticipantStatusEnum.SENT,
    sentAt: row.sentAt ?? null,
    respondedAt: row.respondedAt ?? null,
    globalScore: row.globalScore ?? null,
    // Per-question answers aren't in the tracking payload; the response panel drills into
    // getEvaluation(recipientId) for them (useEvaluationResponseStore).
    answers: [],
  };
}

function mapSessionFormSummary(summary: SessionFormSummary): ISessionSurvey {
  const type = toEvaluationType(summary.type);
  return {
    id: summary.sessionFormId,
    type,
    templateId: summary.templateId,
    templateName: summary.templateName,
    status: SURVEY_STATUS_FROM_SDK[summary.status] ?? SessionSurveyStatusEnum.SENT,
    sentAt: null,
    schedule: mapOffsetToSchedule(summary.offset),
    recipients:
      type === EvaluationTypeEnum.DIRECT
        ? SessionSurveyRecipientEnum.PARTICIPANTS
        : SessionSurveyRecipientEnum.MANAGERS,
    questions: [],
    participants: summary.recipients.map(mapRecipientRow),
  };
}

/** Session forms tracking → the recap KPIs + the hot/cold surveys the tab renders. */
export function mapSessionFormsTracking(response: SessionFormsTrackingResponse): {
  recap: ISessionSurveyRecap;
  surveys: ISessionSurvey[];
} {
  // Response rates come back as 0–1; the tab renders them with a "%" suffix, so scale to 0–100.
  const toPct = (rate: number | null): number | null => (rate == null ? null : Math.round(rate * 100));
  return {
    recap: {
      avgHot: response.kpis.avgHot,
      avgCold: response.kpis.avgCold,
      responseRateHot: toPct(response.kpis.responseRateHot),
      responseRateCold: toPct(response.kpis.responseRateCold),
    },
    // A questionnaire is an « Étape » of the Admin tab, never an enquête of this one.
    surveys: response.forms.filter((form) => form.type !== EvaluationTypeEnum.QUESTIONNAIRE).map(mapSessionFormSummary),
  };
}

/** "Nouvelle enquête" payload → createSessionForm DTO (the template's type drives hot/cold audience). */
export function toCreateSessionFormDto(payload: ICreateSessionSurveyPayload): CreateSessionFormDto {
  const scheduled = payload.sendMode === SessionSurveySendModeEnum.SCHEDULED;
  return {
    templateId: payload.templateId,
    sendMode: scheduled ? CreateSessionFormDtoSendModeEnum.Scheduled : CreateSessionFormDtoSendModeEnum.Now,
    offset:
      scheduled && payload.schedule
        ? {
            n: payload.schedule.amount,
            unit: OFFSET_UNIT_TO_SDK[payload.schedule.unit],
            direction: OFFSET_DIRECTION_TO_SDK[payload.schedule.direction],
            anchor: OFFSET_ANCHOR_TO_SDK[payload.schedule.anchor],
          }
        : null,
  };
}
